/* eslint-disable react-refresh/only-export-components */
import { DimeButton, DimeSeparator } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/button')({
  component: ButtonDocs,
});

type Variant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link'
  | 'success-solid'
  | 'destructive-outline'
  | 'destructive-ghost';
type Size = 'sm' | 'default' | 'lg' | 'touch';

const VARIANTS: { value: Variant; label: string; description: string }[] = [
  { value: 'default', label: 'Default', description: 'Main call-to-action. Use sparingly.' },
  { value: 'secondary', label: 'Secondary', description: 'Alternate, lower-emphasis action.' },
  { value: 'destructive', label: 'Destructive', description: 'Irreversible or dangerous action.' },
  { value: 'outline', label: 'Outline', description: 'Subtle bordered button.' },
  { value: 'ghost', label: 'Ghost', description: 'No background, hover only.' },
  { value: 'link', label: 'Link', description: 'Looks like a text link.' },
  {
    value: 'success-solid',
    label: 'Success Solid',
    description: 'Filled green button for confirming a positive outcome, such as checking in.',
  },
  {
    value: 'destructive-outline',
    label: 'Destructive Outline',
    description: 'Outline button with red text, for delete and remove icon buttons.',
  },
  {
    value: 'destructive-ghost',
    label: 'Destructive Ghost',
    description: 'Ghost button with red text, for removing a row in an editable table.',
  },
];

const SIZES: { value: Size; label: string }[] = [
  { value: 'sm', label: 'Small' },
  { value: 'default', label: 'Default' },
  { value: 'lg', label: 'Large' },
  { value: 'touch', label: 'Touch (44px)' },
];

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function ButtonDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Button</h1>
      <p className="mb-8 text-muted-foreground">
        Triggers actions or navigation. Choose a variant that matches the weight of the action.
      </p>

      <DocsSection title="Variants">
        <Preview>
          {VARIANTS.map((variant) => (
            <DimeButton key={variant.value} variant={variant.value}>
              {variant.label}
            </DimeButton>
          ))}
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Variant</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {VARIANTS.map((variant) => (
              <tr key={variant.value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{variant.value}</td>
                <td className="py-2 text-muted-foreground">{variant.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          {SIZES.map((size) => (
            <DimeButton key={size.value} size={size.value}>
              {size.label}
            </DimeButton>
          ))}
        </Preview>
      </DocsSection>

      <DocsSection title="Disabled state">
        <Preview>
          {VARIANTS.map((variant) => (
            <DimeButton key={variant.value} variant={variant.value} disabled>
              {variant.label}
            </DimeButton>
          ))}
        </Preview>
      </DocsSection>
    </div>
  );
}
